import { supabase } from "@/lib/supabase/browser-client"
import { authenticatedFetch } from "@/lib/auth/authenticated-fetch"
import { normalizeWhiteboardCanvasData } from "@/lib/spaces/whiteboards/canvas-data"
import { Database } from "@/supabase/types"
import { SupabaseClient } from "@supabase/supabase-js"
import type { WhiteboardCanvasData } from "@/types/spaces"

type SpaceWhiteboard = Database["public"]["Tables"]["space_whiteboards"]["Row"]
type SpaceWhiteboardInsert = Database["public"]["Tables"]["space_whiteboards"]["Insert"]
type SpaceWhiteboardUpdate = Database["public"]["Tables"]["space_whiteboards"]["Update"]

type WhiteboardMutationOptions = {
  spaceId?: string | null
  supabaseClient?: SupabaseClient<Database>
}

const normalizeWhiteboardRecord = (
  whiteboard: SpaceWhiteboard
): SpaceWhiteboard => {
  if (!whiteboard.canvas_data) {
    return whiteboard
  }

  return {
    ...whiteboard,
    canvas_data: normalizeWhiteboardCanvasData(whiteboard.canvas_data, {
      fallbackVersion: whiteboard.version ?? 1
    })
  }
}

const readSpaceRouteErrorMessage = async (
  response: Response,
  fallback: string
) => {
  const responseText = await response.text()

  if (!responseText) {
    return fallback
  }

  try {
    const payload = JSON.parse(responseText) as unknown

    if (
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      typeof payload.error === "string"
    ) {
      return payload.error
    }
  } catch {
    return responseText
  }

  return responseText
}

const isSupabaseClient = (
  value: unknown
): value is SupabaseClient<Database> => {
  return (
    typeof value === "object" &&
    value !== null &&
    "from" in value &&
    typeof (value as SupabaseClient<Database>).from === "function"
  )
}

const resolveWhiteboardMutationOptions = (
  optionsOrClient?: SupabaseClient<Database> | WhiteboardMutationOptions
) => {
  if (!optionsOrClient) {
    return {
      spaceId: null,
      supabaseClient: null
    }
  }

  if (isSupabaseClient(optionsOrClient)) {
    return {
      spaceId: null,
      supabaseClient: optionsOrClient
    }
  }

  return {
    spaceId:
      typeof optionsOrClient.spaceId === "string" &&
      optionsOrClient.spaceId.trim().length > 0
        ? optionsOrClient.spaceId
        : null,
    supabaseClient: optionsOrClient.supabaseClient ?? null
  }
}

const resolveBrowserSpaceIdFromPathname = () => {
  if (typeof window === "undefined") {
    return null
  }

  const pathnameMatch = window.location.pathname.match(
    /\/spaces\/([0-9a-f-]{36})/i
  )

  return pathnameMatch?.[1] ?? null
}

const resolveSpaceId = (
  ...spaceIds: Array<string | null | undefined>
) => {
  for (const value of spaceIds) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value
    }
  }

  return null
}

export const createSpaceWhiteboard = async (
  whiteboard: SpaceWhiteboardInsert,
  optionsOrClient?: SupabaseClient<Database> | WhiteboardMutationOptions
): Promise<SpaceWhiteboard> => {
  const options = resolveWhiteboardMutationOptions(optionsOrClient)
  const routeSpaceId = resolveSpaceId(
    options.spaceId,
    whiteboard.space_id,
    resolveBrowserSpaceIdFromPathname()
  )

  if (!options.supabaseClient && typeof window !== "undefined" && routeSpaceId) {
    const response = await authenticatedFetch(`/api/spaces/${routeSpaceId}/whiteboards`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(whiteboard)
    })

    if (!response.ok) {
      throw new Error(
        await readSpaceRouteErrorMessage(
          response,
          "Failed to create whiteboard"
        )
      )
    }

    return normalizeWhiteboardRecord((await response.json()) as SpaceWhiteboard)
  }

  const client = options.supabaseClient || supabase
  const { data, error } = await client
    .from("space_whiteboards")
    .insert([whiteboard])
    .select()
    .single()

  if (error) {
    console.error("Error creating whiteboard:", error)
    throw new Error(`Failed to create whiteboard: ${error.message}`)
  }

  return normalizeWhiteboardRecord(data)
}

export const getSpaceWhiteboards = async (spaceId: string): Promise<SpaceWhiteboard[]> => {
  if (typeof window !== "undefined") {
    const response = await authenticatedFetch(`/api/spaces/${spaceId}/whiteboards`, {
      cache: "no-store"
    })

    if (!response.ok) {
      throw new Error(
        await readSpaceRouteErrorMessage(
          response,
          "Failed to fetch whiteboards"
        )
      )
    }

    return ((await response.json()) as SpaceWhiteboard[]).map(
      normalizeWhiteboardRecord
    )
  }

  const { data, error } = await supabase
    .from("space_whiteboards")
    .select("*")
    .eq("space_id", spaceId)
    .order("created_at", { ascending: false })

  if (error) {
    console.error("Error fetching whiteboards:", error)
    throw new Error(`Failed to fetch whiteboards: ${error.message}`)
  }

  return (data || []).map(normalizeWhiteboardRecord)
}

export const getSpaceWhiteboard = async (
  spaceId: string,
  whiteboardId: string
): Promise<SpaceWhiteboard | null> => {
  if (typeof window !== "undefined") {
    const response = await authenticatedFetch(
      `/api/spaces/${spaceId}/whiteboards/${whiteboardId}`,
      {
        cache: "no-store"
      }
    )

    if (response.status === 404) {
      return null
    }

    if (!response.ok) {
      throw new Error(
        await readSpaceRouteErrorMessage(
          response,
          "Failed to fetch whiteboard"
        )
      )
    }

    return normalizeWhiteboardRecord((await response.json()) as SpaceWhiteboard)
  }

  const { data, error } = await supabase
    .from("space_whiteboards")
    .select("*")
    .eq("space_id", spaceId)
    .eq("id", whiteboardId)
    .maybeSingle()

  if (error) {
    console.error("Error fetching whiteboard:", error)
    throw new Error(`Failed to fetch whiteboard: ${error.message}`)
  }

  return data ? normalizeWhiteboardRecord(data) : null
}

export const getWhiteboardById = async (id: string): Promise<SpaceWhiteboard | null> => {
  const { data, error } = await supabase
    .from("space_whiteboards")
    .select("*")
    .eq("id", id)
    .single()

  if (error) {
    if (error.code === "PGRST116") {
      return null // Not found
    }
    console.error("Error fetching whiteboard:", error)
    throw new Error(`Failed to fetch whiteboard: ${error.message}`)
  }

  return normalizeWhiteboardRecord(data)
}

export const updateSpaceWhiteboard = async (
  id: string,
  updates: SpaceWhiteboardUpdate,
  optionsOrClient?: SupabaseClient<Database> | WhiteboardMutationOptions
): Promise<SpaceWhiteboard> => {
  const options = resolveWhiteboardMutationOptions(optionsOrClient)
  const routeSpaceId = resolveSpaceId(
    options.spaceId,
    typeof updates.space_id === "string" ? updates.space_id : null,
    resolveBrowserSpaceIdFromPathname()
  )

  if (!options.supabaseClient && typeof window !== "undefined" && routeSpaceId) {
    const response = await authenticatedFetch(
      `/api/spaces/${routeSpaceId}/whiteboards/${id}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(updates)
      }
    )

    if (!response.ok) {
      throw new Error(
        await readSpaceRouteErrorMessage(
          response,
          "Failed to update whiteboard"
        )
      )
    }

    return normalizeWhiteboardRecord((await response.json()) as SpaceWhiteboard)
  }

  const client = options.supabaseClient || supabase
  const { data, error } = await client
    .from("space_whiteboards")
    .update(updates)
    .eq("id", id)
    .select()
    .single()

  if (error) {
    console.error("Error updating whiteboard:", error)
    throw new Error(`Failed to update whiteboard: ${error.message}`)
  }

  return normalizeWhiteboardRecord(data)
}

export const deleteSpaceWhiteboard = async (
  id: string,
  optionsOrClient?: SupabaseClient<Database> | WhiteboardMutationOptions
): Promise<void> => {
  const options = resolveWhiteboardMutationOptions(optionsOrClient)
  const routeSpaceId = resolveSpaceId(
    options.spaceId,
    resolveBrowserSpaceIdFromPathname()
  )

  if (!options.supabaseClient && typeof window !== "undefined" && routeSpaceId) {
    const response = await authenticatedFetch(
      `/api/spaces/${routeSpaceId}/whiteboards/${id}`,
      {
        method: "DELETE"
      }
    )

    if (!response.ok) {
      throw new Error(
        await readSpaceRouteErrorMessage(
          response,
          "Failed to delete whiteboard"
        )
      )
    }

    return
  }

  const client = options.supabaseClient || supabase
  const { error } = await client.from("space_whiteboards").delete().eq("id", id)

  if (error) {
    console.error("Error deleting whiteboard:", error)
    throw new Error(`Failed to delete whiteboard: ${error.message}`)
  }
}

export const updateWhiteboardCanvas = async (
  id: string,
  canvasData: WhiteboardCanvasData,
  options?: {
    version?: number | null
    lastEditedBy?: string | null
    spaceId?: string | null
    supabaseClient?: SupabaseClient<Database>
  }
): Promise<SpaceWhiteboard> => {
  const normalizedCanvasData = normalizeWhiteboardCanvasData(canvasData, {
    fallbackVersion: options?.version ?? canvasData.version ?? 1
  }) || canvasData

  const mutationOptions = resolveWhiteboardMutationOptions({
    spaceId: options?.spaceId,
    supabaseClient: options?.supabaseClient
  })
  const routeSpaceId = resolveSpaceId(
    mutationOptions.spaceId,
    resolveBrowserSpaceIdFromPathname()
  )

  if (
    !mutationOptions.supabaseClient &&
    typeof window !== "undefined" &&
    routeSpaceId
  ) {
    const response = await authenticatedFetch(
      `/api/spaces/${routeSpaceId}/whiteboards/${id}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          canvasData: normalizedCanvasData,
          version: options?.version ?? normalizedCanvasData.version ?? null
        })
      }
    )

    if (!response.ok) {
      throw new Error(
        await readSpaceRouteErrorMessage(
          response,
          "Failed to update whiteboard canvas"
        )
      )
    }

    return normalizeWhiteboardRecord((await response.json()) as SpaceWhiteboard)
  }

  const client = mutationOptions.supabaseClient || supabase
  const { data, error } = await client
    .from("space_whiteboards")
    .update({
      canvas_data: normalizedCanvasData,
      version: options?.version ?? normalizedCanvasData.version ?? undefined,
      last_edited_by: options?.lastEditedBy ?? undefined,
      updated_at: new Date().toISOString()
    })
    .eq("id", id)
    .select()
    .single()

  if (error) {
    console.error("Error updating whiteboard canvas:", error)
    throw new Error(`Failed to update whiteboard canvas: ${error.message}`)
  }

  return normalizeWhiteboardRecord(data)
}
